import React, { createContext, useContext, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import AppModal from '../components/ui/AppModal';
import AppToast from '../components/ui/AppToast';
import { ACTION_NETWORK_ERROR_EVENT } from '../constants/network';

// ─── PRESETS ─────────────────────────────────────────────────────────────────

const ICON_PRESET = {
  success: '✅',
  error:   '❌',
  warning: '⚠️',
  info:    'ℹ️',
};

// -1 = stays until manually dismissed
const TOAST_DURATION = {
  success: 6000,
  info:    6000,
  warning: 9000,
  error:   -1,
};

const MAX_VISIBLE_TOASTS = 4;
const SESSION_EXPIRED_EVENT = 'session-expired';
const SESSION_RESTORED_EVENT = 'session-restored';

// ─── UUID (future: will map to DB primary key) ────────────────────────────────

const genId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

// ─── CONTEXT ─────────────────────────────────────────────────────────────────

const NotifyContext = createContext(null);

export const useNotify = () => {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error('useNotify must be used within <NotifyProvider>');
  return ctx;
};

// ─── PROVIDER ─────────────────────────────────────────────────────────────────

export const NotifyProvider = ({ children }) => {

  // ── NOTIFICATION STORE ────────────────────────────────────────────────────
  // This is the persistent list — structured for future DB sync:
  //   • id        → will be the DB primary key
  //   • timestamp → used for 30-day auto-delete filter
  //   • read      → for mark-as-read feature
  // When the DB table is ready:
  //   1. Fetch this list from API on mount (replaceNotifications)
  //   2. POST each new entry to DB in addToast (after setNotifications)
  //   3. Poll / subscribe for updates (periodic refresh)
  const [notifications, setNotifications] = useState([]);

  // ── TOAST DISPLAY QUEUE ───────────────────────────────────────────────────
  // Subset of notifications currently visible on screen.
  // Separate from `notifications` so history is never lost when a toast fades.
  const [activeToasts, setActiveToasts] = useState([]);
  const activeToastsRef = useRef([]);
  const activeKeysRef = useRef(new Set());
  const sessionExpiredRef = useRef(false);

  const replaceActiveToasts = useCallback((toasts) => {
    activeToastsRef.current = toasts;
    activeKeysRef.current = new Set(toasts.map(toastEntry => `${toastEntry.type}|${toastEntry.message}`));
    setActiveToasts(toasts);
  }, []);

  const addToast = useCallback(({ type = 'info', message, title, duration } = {}) => {
    if (type === 'error' && sessionExpiredRef.current) return null;

    const key = `${type}|${message}`;
    if (activeKeysRef.current.has(key)) return null;

    const entry = {
      id:        genId(),
      type,
      message,
      title:     title ?? null,
      timestamp: Date.now(),
      duration:  duration ?? TOAST_DURATION[type] ?? 3500,
      read:      false,
    };

    // future: await api.createNotification(entry)
    setNotifications(prev => [entry, ...prev]);
    replaceActiveToasts([...activeToastsRef.current, entry].slice(-MAX_VISIBLE_TOASTS));

    return entry.id;
  }, [replaceActiveToasts]);

  const dismissToast = useCallback((id) => {
    replaceActiveToasts(activeToastsRef.current.filter(t => t.id !== id));
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    // future: await api.markNotificationRead(id)
  }, [replaceActiveToasts]);

  // Shorthand helpers
  const toast = useMemo(() => ({
    success: (message, opts) => addToast({ type: 'success', message, ...opts }),
    error:   (message, opts) => addToast({ type: 'error',   message, ...opts }),
    warning: (message, opts) => addToast({ type: 'warning', message, ...opts }),
    info:    (message, opts) => addToast({ type: 'info',    message, ...opts }),
    dismiss: (id) => dismissToast(id),
  }), [addToast, dismissToast]);

  useEffect(() => {
    const onActionFailure = ({ detail }) => {
      if (detail?.message) toast.error(detail.message);
    };
    window.addEventListener(ACTION_NETWORK_ERROR_EVENT, onActionFailure);
    return () => window.removeEventListener(ACTION_NETWORK_ERROR_EVENT, onActionFailure);
  }, [toast]);

  useEffect(() => {
    const handleSessionExpired = () => {
      sessionExpiredRef.current = true;
      replaceActiveToasts(activeToastsRef.current.filter(toastEntry => toastEntry.type !== 'error'));
    };
    const handleSessionRestored = () => {
      sessionExpiredRef.current = false;
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(SESSION_RESTORED_EVENT, handleSessionRestored);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(SESSION_RESTORED_EVENT, handleSessionRestored);
    };
  }, [replaceActiveToasts]);

  // ── MODAL (Promise-based) ─────────────────────────────────────────────────
  // One modal at a time. `modal(config)` returns a Promise that resolves
  // with the `value` of whichever button the user clicked (or null if dismissed).
  const [modalConfig, setModalConfig] = useState(null);
  const resolverRef = useRef(null);

  const modal = useCallback((config) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setModalConfig(config);
    });
  }, []);

  const resolveModal = useCallback((value) => {
    setModalConfig(null);
    resolverRef.current?.(value);
    resolverRef.current = null;
  }, []);

  const contextValue = useMemo(
    () => ({ toast, modal, notifications }),
    [toast, modal, notifications]
  );

  return (
    <NotifyContext.Provider value={contextValue}>
      {children}

      {/* Always mounted at root — guaranteed to be above every page */}
      <AppModal config={modalConfig} onResolve={resolveModal} iconPreset={ICON_PRESET} />
      <AppToast toasts={activeToasts}  onDismiss={dismissToast} iconPreset={ICON_PRESET} />
    </NotifyContext.Provider>
  );
};
